import { useMemo, useRef, useState } from 'react';
import type { QueryFilter, SegmentsTimeFrame } from '../model/types';
import { classNames } from '@/shared/lib/classNames';
import { Segments, SegmentsRef } from '@/features/SegmentsNew';
import { useResize } from '@/shared/lib/hooks/useResize/useResize';
import { DatePickerRange, DatePickerRangeProps } from '@salutejs/sdds-insol';
import { CORRECT_DATA_REGEX } from '../model/data';
import { getTimeFrames } from '../lib/getTimeFrames';
import arrowRight from '@/shared/assets/icons/arrowRight.png';
import Calendar from '@/shared/assets/icons/calendar.svg?react';
import cls from './ChartControls.module.scss';

const getCalendarContainerWidth = (width: number) => {
    if (width > 1023) {
        return undefined;
    }

    return '100vw';
};

type Props = {
    calendarOffset: [number, number];
    className?: string;
    maxDate: Date;
    minDate: Date;
    size: 'l' | 'm' | 's' | 'xs';
    onChange: (timeFrame: QueryFilter) => void;
};

export const ChartControls = ({ className, minDate, maxDate, calendarOffset, onChange, size }: Props) => {
    const segmentsRef = useRef<SegmentsRef>(null);
    const [datePickerValue, setDatePickerValue] = useState<DatePickerRangeProps['value']>([undefined, undefined]);

    const width = useResize();

    const timeFrames = useMemo(() => {
        return getTimeFrames(width);
    }, []);

    return (
        <div className={classNames(cls.container, {}, [className])} id="chartControls">
            <Segments
                className={cls.segments}
                size={size}
                ref={segmentsRef}
                items={timeFrames}
                initialValue={timeFrames[3].value}
                onChange={(value) => {
                    setDatePickerValue([undefined, undefined]);
                    onChange({
                        segmentsValue: value as SegmentsTimeFrame,
                        timeRange: null,
                    });
                }}
            />
            <DatePickerRange
                size={size}
                type="Months"
                max={maxDate}
                min={minDate}
                format="MM.YYYY"
                autoComplete="true"
                dividerVariant="icon"
                value={datePickerValue}
                offset={calendarOffset}
                firstPlaceholder="мм.гг"
                secondPlaceholder="мм.гг"
                secondTextfieldContentRight={<Calendar />}
                calendarContainerWidth={getCalendarContainerWidth(width)}
                dividerIcon={<img className={cls.arrow} src={arrowRight} alt="arrowRight" />}
                maskWithFormat
                isDoubleCalendar
                onChange={(event: { target: { name?: string | undefined; value?: string | undefined } }) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const [firstValue, _, secondValue] = event.target!.value!.split(' ');

                    const isDatesValid = CORRECT_DATA_REGEX.test(firstValue) && CORRECT_DATA_REGEX.test(secondValue);

                    if (!isDatesValid) {
                        return;
                    }

                    onChange({
                        segmentsValue: null,
                        timeRange: {
                            startDate: firstValue.split('.').reverse().join('-'),
                            endDate: secondValue.split('.').reverse().join('-'),
                        },
                    });
                    segmentsRef.current?.reset();
                    setDatePickerValue([firstValue, secondValue]);
                }}
            />
        </div>
    );
};
